import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { StorageControlsPatternFormat } from '../../../types';
import {
	__internalGetPatternFormat,
	__internalGetResolvedFlatPatternFileFormat,
	__internalSetPatternFormat,
	__internalSetResolvedFlatPatternFileFormat,
} from '../bsc-global-state/core';
import { isSSR } from '../is-ssr';

/**
 * SSR-aware evaluation of the BSC flat pattern file format rollout.
 *
 * The frontend and SSR paths rolled out on independent schedules (COOK-577): reading one shared
 * flag from both caused Confluence SSR/hydration mismatches. The frontend rollout (non-SSR) is now
 * fully launched, so that path is always flat. In an SSR context we still prefer the write-once
 * value resolved by Confluence and fall back to the SSR-specific gate, which has its own rollout.
 *
 * Prefer {@link getFetchPatternFormat} over calling this directly — it evaluates this once per
 * initialization. `fg` is not stable within a process (an unresolved Statsig client returns `false`,
 * then `true`), so evaluating it separately at each site that needs the answer can produce
 * disagreeing results. Never call it to decide how to *read* a payload: read `patternFormat` off the
 * payload instead (COOK-632).
 */
export const isFlatPatternFileFormatEnabled = (): boolean => {
	if (!isSSR()) {
		return true;
	}

	return (
		__internalGetResolvedFlatPatternFileFormat() ?? fg('platform_bsc_ssr_flat_pattern_file_format')
	);
};

/**
 * Seeds SSR flag evaluation from a value that has already been resolved by Confluence.
 *
 * UNSAFE: This exists exclusively for Confluence's temporary COOK-557 rollout. Do not use it from
 * other products. Remove it when the related format flags are cleaned up.
 */
export const UNSAFE__setFetchPatternFormatFromResolvedFlag = (value: boolean): void => {
	__internalSetResolvedFlatPatternFileFormat(value);
};

/**
 * Which category-file layout this process fetches.
 *
 * This is the only place the rollout gate is consulted for that purpose. The first caller to ask
 * decides, the answer is held in bsc-global-state, and every later caller reuses it — so two sites
 * cannot end up fetching different layouts because `fg` resolved differently between them. The first
 * caller is usually `initializeControls`, which seeds the frontend gate immediately before building
 * its `CacheLoader`s. In Confluence's Bifrost worker the resolved SSR gate can seed it earlier while
 * handling a request.
 *
 * Nothing revises the decision, `initializeControls` included. If the first caller asks before the
 * flag client resolves, this process stays on that answer for its lifetime: consistently reading
 * payloads in the layout they were fetched in matters more than tracking the rollout promptly, because
 * a revised answer would leave already-fetched payloads being read as the other layout.
 */
export const getFetchPatternFormat = (): StorageControlsPatternFormat => {
	const decided = __internalGetPatternFormat();
	if (decided) {
		return decided;
	}

	const patternFormat = isFlatPatternFileFormatEnabled() ? 'flat' : 'trie';
	__internalSetPatternFormat(patternFormat);
	return patternFormat;
};
